import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { FileText, ShieldCheck, Eye, CheckCircle, X } from "lucide-react";

type LegalDocument = {
  id: number;
  title: string;
  file_url: string;
};

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/legal-documents`;
const defaultImage = `${siteUrl}/logo512.png`;
const backendUrl = "https://www.sevaafoundation.com/seva-backend";

const seoKeywords = [
  "Sevaa Foundation legal documents",
  "Swadesh Sevaa Charitable Foundation legal documents",
  "NGO legal documents India",
  "registered NGO documents",
  "NGO registration certificate",
  "80G certificate NGO",
  "12A certificate NGO",
  "CSR-1 certificate NGO",
  "charitable trust documents",
  "NGO certification India",
  "NGO transparency documents",
  "NGO trust certificate",
  "donation tax exemption NGO",
  "80G tax exemption donation",
  "CSR eligible NGO India",
  "NGO compliance documents",
  "Sevaa Foundation certificates",
  "legal NGO India",
  "verified NGO India",
  "charity trust registration",
  "समाज सेवा संस्था दस्तावेज",
  "NGO कानूनी दस्तावेज",
  "रजिस्टर्ड NGO",
  "सेवा फाउंडेशन दस्तावेज",
  "चैरिटेबल ट्रस्ट प्रमाणपत्र",
  "80G प्रमाणपत्र",
  "CSR NGO प्रमाणपत्र",
  "दान टैक्स छूट",
  "NGO registration certificate India",
];

const LegalDocs = () => {
  const [docs, setDocs] = useState<LegalDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState<LegalDocument | null>(null);

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/docs/list_docs.php`);
        const data = await res.json();

        if (data.success) {
          setDocs(data.documents);
        }
      } catch (error) {
        console.error("Failed to load documents", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocs();
  }, []);

  const getDocumentViewUrl = (fileUrl: string) => {
    return `${backendUrl}${fileUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`;
  };

  const legalDocsSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Legal Documents | Sevaa Foundation",
    alternateName: "Sevaa Foundation Legal and Certification Documents",
    url: pageUrl,
    inLanguage: ["en-IN", "hi-IN"],
    description:
      "View Sevaa Foundation legal documents, registration certificates, tax exemption documents, CSR eligibility and NGO transparency documents for public verification.",
    isPartOf: {
      "@type": "WebSite",
      name: "Sevaa Foundation",
      url: siteUrl,
    },
    publisher: {
      "@type": "NGO",
      name: "Sevaa Foundation",
      alternateName: "Swadesh Sevaa Charitable Foundation",
      url: siteUrl,
      logo: defaultImage,
      areaServed: {
        "@type": "Country",
        name: "India",
      },
    },
    about: [
      "NGO Legal Documents",
      "NGO Registration Certificate",
      "80G Tax Exemption",
      "CSR Eligibility",
      "Charitable Trust Documents",
      "NGO Transparency",
      "Donation Tax Exemption",
      "Legal Compliance",
    ],
    hasPart: docs.map((doc) => ({
      "@type": "DigitalDocument",
      name: doc.title,
      url: `${pageUrl}#document-${doc.id}`,
      description: `${doc.title} official legal document of Sevaa Foundation available for transparency verification`,
      encodingFormat: "application/pdf",
      publisher: {
        "@type": "NGO",
        name: "Sevaa Foundation",
        url: siteUrl,
      },
    })),
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "NGO",
    name: "Sevaa Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: defaultImage,
    description:
      "Sevaa Foundation is a registered NGO working for child education, healthcare, women empowerment, hunger support and social welfare in India.",
    areaServed: {
      "@type": "Country",
      name: "India",
    },
    knowsAbout: [
      "Child Education",
      "Healthcare Support",
      "Women Empowerment",
      "Hunger Relief",
      "Food Donation",
      "CSR Partnership",
      "Donation",
      "Volunteer Work",
      "Social Welfare",
      "NGO Legal Compliance",
    ],
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: siteUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Legal Documents",
        item: pageUrl,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>
          Legal Documents | Sevaa Foundation NGO Registration, 80G, CSR & Trust
          Certificates
        </title>

        <meta
          name="title"
          content="Legal Documents | Sevaa Foundation NGO Registration, 80G, CSR & Trust Certificates"
        />

        <meta
          name="description"
          content="View Sevaa Foundation legal documents, NGO registration certificate, 80G tax exemption, CSR eligibility, charitable trust certificates and transparency documents for public verification."
        />

        <meta
          name="keywords"
          content="Sevaa Foundation legal documents, Swadesh Sevaa Charitable Foundation documents, NGO legal documents India, registered NGO documents, NGO registration certificate, 80G certificate NGO, 12A certificate NGO, CSR-1 certificate NGO, charitable trust documents, NGO certification India, NGO transparency documents, NGO trust certificate, donation tax exemption NGO, 80G tax exemption donation, CSR eligible NGO India, NGO compliance documents, verified NGO India, charity trust registration, समाज सेवा संस्था दस्तावेज, NGO कानूनी दस्तावेज, रजिस्टर्ड NGO, सेवा फाउंडेशन दस्तावेज, चैरिटेबल ट्रस्ट प्रमाणपत्र, 80G प्रमाणपत्र, CSR NGO प्रमाणपत्र, दान टैक्स छूट"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Sevaa Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Sevaa Foundation" />
        <meta property="og:url" content={pageUrl} />

        <meta
          property="og:title"
          content="Legal Documents | Sevaa Foundation NGO Certificates"
        />

        <meta
          property="og:description"
          content="Verify Sevaa Foundation's NGO registration, legal documents, 80G tax exemption, CSR eligibility and charitable trust certificates."
        />

        <meta property="og:image" content={defaultImage} />
        <meta
          property="og:image:alt"
          content="Sevaa Foundation Legal Documents and NGO Certificates"
        />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="Legal Documents | Sevaa Foundation NGO Certificates"
        />

        <meta
          name="twitter:description"
          content="View Sevaa Foundation legal documents, NGO registration certificates, 80G, CSR eligibility and transparency documents."
        />

        <meta name="twitter:image" content={defaultImage} />

        <script type="application/ld+json">
          {JSON.stringify(legalDocsSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-slate-50 min-h-screen pb-20 font-poppins">
        {/* Hero Section */}
        <section
          aria-labelledby="legal-docs-heading"
          className="bg-emerald-900 text-white py-20 px-6 text-center relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 p-10 opacity-10" aria-hidden="true">
            <ShieldCheck size={200} />
          </div>

          <div className="relative z-10 max-w-3xl mx-auto">
            <span className="bg-emerald-800 text-emerald-300 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4 inline-block">
              100% Transparent
            </span>

            <h1
              id="legal-docs-heading"
              className="text-4xl md:text-5xl font-black mb-4"
            >
              Legal & Certifications
            </h1>

            <p className="text-emerald-100 text-lg leading-relaxed">
              We operate with complete transparency. All our registration, tax
              exemption, CSR eligibility and operational documents are available
              for public verification.
            </p>

            <p className="sr-only">
              Sevaa Foundation legal documents, NGO registration certificate,
              80G certificate, CSR certificate, charitable trust documents, NGO
              transparency, registered NGO India, सेवा फाउंडेशन कानूनी
              दस्तावेज, 80G प्रमाणपत्र, चैरिटेबल ट्रस्ट प्रमाणपत्र.
            </p>
          </div>
        </section>

        <section
          aria-labelledby="trust-indicators-heading"
          className="max-w-5xl mx-auto px-6 -mt-8 relative z-20"
        >
          <h2 id="trust-indicators-heading" className="sr-only">
            Sevaa Foundation Trust and Legal Indicators
          </h2>

          {/* Trust Indicators */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 flex flex-wrap justify-center gap-6 md:gap-12 mb-12">
            <div className="flex items-center gap-2 text-slate-700 font-semibold">
              <CheckCircle className="text-emerald-500" size={20} />
              Govt. Registered NGO
            </div>

            <div className="flex items-center gap-2 text-slate-700 font-semibold">
              <CheckCircle className="text-emerald-500" size={20} />
              80G Tax Exemption
            </div>

            <div className="flex items-center gap-2 text-slate-700 font-semibold">
              <CheckCircle className="text-emerald-500" size={20} />
              CSR Eligible (CSR-1)
            </div>
          </div>

          {isLoading ? (
            <div className="py-20 text-center text-emerald-600 font-bold">
              Loading documents...
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {docs.length === 0 && (
                <div className="col-span-full py-20 text-center text-slate-500 bg-white rounded-2xl border border-dashed border-slate-300">
                  Documents are being updated. Please check back later.
                </div>
              )}

              {docs.map((doc) => (
                <article
                  key={doc.id}
                  id={`document-${doc.id}`}
                  className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all flex items-center justify-between group"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                      aria-hidden="true"
                    >
                      <FileText size={24} />
                    </div>

                    <div>
                      <h2 className="font-bold text-slate-800 text-lg">
                        {doc.title}
                      </h2>

                      <p className="text-xs text-slate-400 font-medium mt-0.5">
                        Official Document | Sevaa Foundation
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedDoc(doc)}
                      className="p-2.5 bg-slate-50 hover:bg-slate-900 hover:text-white text-slate-600 rounded-xl transition-colors"
                      title="View Document"
                      aria-label={`View ${doc.title} document`}
                    >
                      <Eye size={18} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* SEO Keyword Trust Tags */}
          {/* <div className="mt-14 flex flex-wrap justify-center gap-2">
            {seoKeywords.map((keyword) => (
              <span
                key={keyword}
                className="rounded-full bg-white border border-slate-200 px-3 py-1.5 text-[10px] md:text-xs font-bold text-slate-500 shadow-sm"
              >
                {keyword}
              </span>
            ))}
          </div> */}
        </section>
      </main>

      {/* DOCUMENT VIEW MODAL WITH WATERMARK */}
      {selectedDoc && (
        <div className="fixed inset-0 z-[9999] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-3 md:p-6">
          <div className="bg-white w-full max-w-6xl h-[92vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            <div className="flex items-center justify-between gap-4 px-4 md:px-6 py-4 border-b border-slate-200 bg-white">
              <div>
                <h3 className="font-bold text-slate-800 text-base md:text-xl">
                  {selectedDoc.title}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  View only document for transparency verification
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedDoc(null)}
                className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-900 hover:text-white text-slate-600 flex items-center justify-center transition-colors"
                aria-label="Close document viewer"
              >
                <X size={20} />
              </button>
            </div>

            <div
              className="relative flex-1 bg-slate-100 overflow-hidden"
              onContextMenu={(e) => e.preventDefault()}
            >
              {/* Top Notice Watermark */}
              <div className="absolute top-0 left-0 right-0 z-20 bg-emerald-900/90 text-white text-center text-[10px] md:text-sm font-bold tracking-wide px-3 py-2 pointer-events-none">
                All Rights Reserved by Sevaa Foundation. You are authorized only
                to view this document for transparency verification.
              </div>

              {/* Repeated Diagonal Watermark */}
              <div className="absolute inset-0 z-10 pointer-events-none flex items-center justify-center overflow-hidden">
                <div className="-rotate-35 opacity-10 text-slate-900 text-center select-none">
                  <div className="text-3xl md:text-6xl font-black whitespace-nowrap mb-10">
                    SEVAA FOUNDATION
                  </div>
                  <div className="text-xl md:text-4xl font-black whitespace-nowrap mb-10">
                    VIEW ONLY • NOT FOR DOWNLOAD
                  </div>
                  <div className="text-3xl md:text-6xl font-black whitespace-nowrap">
                    ALL RIGHTS RESERVED
                  </div>
                </div>
              </div>

              <iframe
                src={getDocumentViewUrl(selectedDoc.file_url)}
                title={`${selectedDoc.title} - Sevaa Foundation`}
                className="w-full h-full border-0 pt-9 relative z-0"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LegalDocs;